import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useImperativeHandle,
  useState,
  useMemo,
} from "react";
import { Input, Form, Alert, Modal, message } from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import { FormInstance } from "../../../../hooks/createForm";
import { getCaptchaUrl, getUserRegisterParams } from "../../../../utils/index";
import { User } from "authing-js-sdk";
import {
  isGuiding,
  sessionAccountInfo,
} from "../../../../components/Guidance/utils";
import { Trans, useTranslation } from "react-i18next";
import { SU_ZHOU_YUAN_QU } from "@/pocConfig";
import { ErrorCodes } from "@/constants/enum";
import { useNavigate } from "react-router";
import querystring from "querystring";
import { Rule } from "antd/es/form";
import { i18n } from "@/locales";
import { loginByAccount } from "@/api/login";
import { Agreement, PasswordLoginMethods } from "@/config";
import { ReadAndAccept } from "@/components/ReadAndAccept";

export interface PasswordLoginFormProps
  extends React.HTMLAttributes<HTMLDivElement> {
  onSubmit?: () => void;
  onSuccess?: (user: User) => void;
  onFail?: (error: any) => void;
  onFinishFailed?: (error: any) => void;
  withCustomData?: boolean;
  agreements: Agreement[];

}

const LoginMethodsText: Record<
  PasswordLoginMethods,
  {
    t: string;
    sort: number;
  }
> = {
  [PasswordLoginMethods.EmailPassword]: { t: i18n.t("common.email"), sort: 2 },
  [PasswordLoginMethods.PhonePassword]: {
    t: i18n.t("common.phoneNumber"),
    sort: 1,
  },
  [PasswordLoginMethods.UsernamePassword]: {
    t: i18n.t("common.username"),
    sort: 0,
  },
};

const InternalPasswordLoginForm: ForwardRefRenderFunction<
  FormInstance,
  PasswordLoginFormProps
> = (
  { onSubmit, onSuccess, onFail, onFinishFailed, withCustomData = false, agreements },
  ref
) => {
    const [rawForm] = Form.useForm();
    const config = window.__config__;
    const [needVerifyCode, setNeedVerifyCode] = useState(false);
    const [verifyCodeUrl, setVerifyCodeUrl] = useState<string | null>(null);
    /** 表单是否被提交校验过 */
    const [validated, setValidated] = useState(false);
    const [accepted, setAccepted] = useState(false);

    const { t } = useTranslation();

    const navigate = useNavigate();

    const [remainCount, setRemainCount] = useState(0);

    const [loginFailCheckEnabled] = useState<boolean>(
      !!config?.loginFailCheckEnabled
    );

    const formInitValue = isGuiding() ? sessionAccountInfo.get() : {};

    const usernamePlaceholder = t("common.inputAccount", {
      text: config.passwordTabConfig?.enabledLoginMethods
        ?.map((item) => LoginMethodsText[item])
        .sort((a, b) => a.sort - b.sort)
        .map((item) => item.t)
        .join(" / "),
    });

    const login = async (values: any) => {
      const identity = values.identity && values.identity.trim();
      const password = values.password && values.password.trim();
      const captchaCode = values.captchaCode && values.captchaCode.trim();
      const autoRegister =
        config.ssoPageComponentDisplay.autoRegisterThenLoginHintInfo;
      return await loginByAccount(identity, password, {
        autoRegister,
        captchaCode,
        customData: getUserRegisterParams(),
        withCustomData,
      });
    };

    const onFinish = async (values: any) => {
      let user: User;
      // 重置一下温馨提示～
      setRemainCount(0);

      setValidated(true);

      if (config.agreementEnabled && agreements?.length && !accepted) {
        onFinishFailed?.("error")
        message.error(t('common.loginProtocolTips'))
        return;
      }

      onSubmit && onSubmit();

      try {
        user = await login(values);
        sessionAccountInfo.remove();
        onSuccess && onSuccess(user);
      } catch (_error) {
        const error: any = _error;
        onFail && onFail(error);

        if (error.code === 1639) {
          // 临时 token，需要携带此 token 进行密码重置
          const { token } = error.data;

          // setInitialPassword(true);

          navigate({
            pathname: "/initial-password?",
            search: querystring.stringify(
              Object.assign(querystring.parse(window.location.search), {
                initial_token: token,
              })
            ),
          });
        }

        if (error.code === 2000 && verifyCodeUrl === null) {
          setNeedVerifyCode(true);
          setVerifyCodeUrl(getCaptchaUrl());
        }

        if (error.code === ErrorCodes.PASSWORD_EXPIRED) {
          const { token, forcedCycle } = error.data;

          const jump = () => {
            navigate({
              pathname: "/reset-change-password?",
              search: querystring.stringify(
                Object.assign(querystring.parse(window.location.search), {
                  initial_token: token,
                })
              ),
            });
          };

          Modal.confirm({
            title: t("common.tips"),
            centered: true,
            content: (
              <span style={{ userSelect: "none" }}>
                <Trans
                  i18nKey="common.resetPasswordMessage"
                  tOptions={{ number: forcedCycle }}
                >
                  <span
                    onClick={() => {
                      Modal.destroyAll();
                      jump();
                    }}
                    style={{ marginLeft: 4, color: "#396aff", cursor: "pointer" }}
                  >
                    0
                  </span>
                </Trans>
              </span>
            ),
            onOk: () => {
              jump();
            },
          });
        }
        // 因为后端返回的改成了用户或密码错误，所以这个也要改一下
        if (
          error.code === ErrorCodes.USER_OR_PASSWORD_WRONG &&
          loginFailCheckEnabled
        ) {
          setRemainCount(error.data.remainCount);
          return;
        }

        // 帐号锁定
        if (
          error.code === ErrorCodes.ACCOUNT_LOCK ||
          error.code === ErrorCodes.ACCOUNT_LOCKED
        ) {
          const query = Object.assign(querystring.parse(window.location.search));

          Modal.confirm({
            title: t("common.tips"),
            centered: true,
            className: "authing-account-lock-modal",
            content: (
              <>
                <span className="authing-account-lock">
                  <Trans i18nKey="common.ginFailCheckContent">
                    <a
                      href={`/feedback`}
                      style={{ marginLeft: 4 }}
                      rel="noopener noreferrer"
                    >
                      点此查看帮助文档
                    </a>
                  </Trans>
                </span>
                <span className={`authing-account-lock-${i18n.language}`}></span>
              </>
            ),
            async onOk() {
              navigate({
                pathname: "/feedback?",
                search: querystring.stringify(query),
              });
            },
          });
        }
      }
    };



    useImperativeHandle(ref, () => ({
      submit: async () => {
        try {
          await rawForm.validateFields();

          rawForm.submit();
        } catch (error: any) {
          onFail && onFail(error);
        }
      },
    }));

    // 自定义密码加密 正则
    const passwordRules = useMemo(() => {
      return [
        {
          required: true,
          message: t("login.inputLoginPwd"),
        },
      ];
    }, [t]);

    const identityRules = useMemo(() => {
      const rules: Rule[] = [
        {
          required: true,
          message: usernamePlaceholder,
          whitespace: true
        },
      ];

      const loginMethods = config?.passwordTabConfig?.enabledLoginMethods;

      if (
        loginMethods?.includes(PasswordLoginMethods.EmailPassword) &&
        loginMethods?.length === 1
      ) {
        rules.push({
          type: "email",
          message: t("common.emailFormatError"),
        });
      }

      return rules;
    }, [config, t, usernamePlaceholder]);


    return (
      <>
        <Form
          initialValues={formInitValue}
          form={rawForm}
          onSubmitCapture={onSubmit}
          onFinishFailed={onFinishFailed}
          onFinish={onFinish}
        >
          <button type="submit" hidden></button>

          {config.ssoPageComponentDisplay.userPasswordInput && (
            <>
              {config.ssoPageComponentDisplay.autoRegisterThenLoginHintInfo && (
                <Alert
                  message={
                    SU_ZHOU_YUAN_QU.appIds.includes(config.id)
                      ? SU_ZHOU_YUAN_QU.pwdLoginTabAlert
                      : t("login.autoRegister")
                  }
                  style={{ marginBottom: 24 }}
                />
              )}
              <Form.Item name="identity" rules={identityRules}>
                <Input
                  autoComplete="email,username,tel"
                  size="large"
                  placeholder={usernamePlaceholder}
                  prefix={<UserOutlined style={{ color: "#ddd" }} />}
                />
              </Form.Item>
              <Form.Item name="password" rules={passwordRules}>
                <Input.Password
                  autoComplete="current-password"
                  size="large"
                  visibilityToggle={false}
                  placeholder={t("login.inputLoginPwd")}
                  prefix={<LockOutlined style={{ color: "#ddd" }} />}
                />
              </Form.Item>
              {needVerifyCode && (
                <Form.Item
                  name="captchaCode"
                  rules={[
                    {
                      required: true,
                      message: t("login.inputCaptchaCode"),
                    },
                  ]}
                >
                  <Input
                    autoComplete="one-time-code"
                    size="large"
                    placeholder={t("login.inputCaptchaCode")}
                    addonAfter={
                      <img
                        src={verifyCodeUrl ?? ""}
                        alt={t("login.captchaCode")}
                        style={{ height: "2em", cursor: "pointer" }}
                        onClick={() => setVerifyCodeUrl(getCaptchaUrl())}
                      />
                    }
                  />
                </Form.Item>
              )}
              {loginFailCheckEnabled && remainCount !== 0 && (
                <span
                  style={{
                    marginBottom: 23,
                    fontSize: 12,
                    color: "#E63333",
                    display: "block",
                  }}
                >
                  {t("common.loginFailCheck", {
                    number: remainCount,
                  })}
                </span>
              )}
              {config.agreementEnabled && Boolean(config.agreements?.length) && (
                <>
                  <ReadAndAccept
                    onChange={setAccepted}
                    agreements={agreements}
                    showError={validated}
                  />
                  <div style={{ marginBottom: 20 }}></div>
                </>
              )}
            </>
          )}
        </Form>
      </>
    );
  };

export const PasswordLoginForm = forwardRef<
  FormInstance,
  PasswordLoginFormProps
>(InternalPasswordLoginForm);
